import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { seasonEligibilityMessages } from '@sorare/core/src/lib/glossary';

import { OwnedCardRow } from '../OwnedCardRow';
import { LazyAllLastOwnedCardsFragment } from '../fragments';
import type {
  LazyAllLastOwnedCardsQuery,
  LazyAllLastOwnedCardsQueryVariables,
} from './__generated__/index.graphql';

const LAZY_ALL_LAST_OWNED_CARDS_QUERY = gql`
  query LazyAllLastOwnedCardsQuery($slug: String!, $after: String) {
    anyPlayer(slug: $slug) {
      slug
      inSeasonCards: anyCards(
        ownedByMe: true
        after: $after
        first: 10
        inSeasonEligible: true
        sorts: [RECENTLY_OWNED_FIRST]
      ) {
        ...LazyAllLastOwnedCardsQuery_anyCard
      }
      classicCards: anyCards(
        ownedByMe: true
        after: $after
        first: 10
        classicOnly: true
        sorts: [RECENTLY_OWNED_FIRST]
      ) {
        ...LazyAllLastOwnedCardsQuery_anyCard
      }
    }
    so5 {
      id
      seasonalityName(seasonality: IN_SEASON)
    }
  }
  ${LazyAllLastOwnedCardsFragment}
` as TypedDocumentNode<
  LazyAllLastOwnedCardsQuery,
  LazyAllLastOwnedCardsQueryVariables
>;

const AllLastOwnedCardsRoot = styled(Vertical)`
  width: 280px;
  max-height: 400px;
  overflow: auto;
`;

const AllLastOwnedCardsTitle = styled(LabelS)`
  background-color: var(--c-nd-50);
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
`;

const Title = styled(LabelM)`
  img {
    margin-right: var(--unit);
  }
`;

type Props = {
  playerSlug?: string;
  displayName: string;
  count: number;
};

export const LazyAllLastOwnedCardsByPlayer = ({
  playerSlug,
  displayName,
  count,
}: Props) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    LAZY_ALL_LAST_OWNED_CARDS_QUERY,
    {
      variables: { slug: playerSlug! },
      connection: 'AnyCardInterfaceConnection',
      skip: !playerSlug,
    }
  );

  const { InfiniteScrollLoader: InSeasonInfiniteScrollLoader } =
    useInfiniteScroll(
      () => {
        loadMore(false, {
          after: data?.anyPlayer?.inSeasonCards?.pageInfo.endCursor,
        });
      },
      !!data?.anyPlayer?.inSeasonCards?.pageInfo.hasNextPage,
      loading
    );

  const { InfiniteScrollLoader: ClassicInfiniteScrollLoader } =
    useInfiniteScroll(
      () => {
        loadMore(false, {
          after: data?.anyPlayer?.classicCards?.pageInfo.endCursor,
        });
      },
      !!data?.anyPlayer?.classicCards?.pageInfo.hasNextPage,
      loading
    );
  const { inSeasonCards, classicCards } = data?.anyPlayer || {
    inSeasonCards: { nodes: Array.from({ length: count }) },
    classicCards: { nodes: [] },
  };
  const { seasonalityName } = data?.so5 || {};

  return (
    <AllLastOwnedCardsRoot>
      <Vertical gap={2}>
        <AllLastOwnedCardsTitle className="text-center" color="var(--c-nd-600)">
          <FormattedMessage
            id="marketplace.benchObject.lastOwnedCards.title"
            defaultMessage="You own x{totalCount, number} {playerName} {totalCount, plural, one {card} other {cards}}"
            values={{
              totalCount: count,
              playerName: displayName,
            }}
          />
        </AllLastOwnedCardsTitle>
        <Vertical>
          {inSeasonCards?.nodes?.length > 0 && (
            <>
              <Skeleton loading={loading}>
                <Title
                  bold
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color':
                        'var(--c-gradient-conversionCreditBorder)',
                    } as CSSProperties
                  }
                >
                  <SportSeasonalityIcon
                    seasonality={So5LeaderboardSeasonality.IN_SEASON}
                  />{' '}
                  {seasonalityName}
                </Title>
              </Skeleton>
              {inSeasonCards.nodes.map((c, index) => (
                <Skeleton key={c?.slug || index} loading={!data}>
                  <OwnedCardRow anyCard={c} />
                </Skeleton>
              ))}
            </>
          )}
          {!!data && <InSeasonInfiniteScrollLoader />}
          {inSeasonCards.nodes.length > 0 && classicCards.nodes.length > 0 && (
            <HorizontalDivider />
          )}
          {classicCards.nodes.length > 0 && (
            <Title bold>
              <SportSeasonalityIcon
                seasonality={So5LeaderboardSeasonality.ALL_SEASONS}
              />{' '}
              <FormattedMessage {...seasonEligibilityMessages.CLASSIC} />
            </Title>
          )}
          {classicCards.nodes.map(c => (
            <OwnedCardRow key={c.slug} anyCard={c} />
          ))}
        </Vertical>
        {!!data && <ClassicInfiniteScrollLoader />}
      </Vertical>
    </AllLastOwnedCardsRoot>
  );
};
